/**
 * useCommandRegistry — the React hook that wires the command registry to the
 * live auth/module/theme context (#1514, EPIC #1512).
 *
 * Reads:
 *  - ``usePermissions()``  → preset + has(flag)
 *  - ``useModules()``      → isEnabled(module)
 *  - ``useEffectiveAuth()``→ role (drives the admin SHELL rule + the preview-as
 *                            persona, so a previewing admin sees the persona's
 *                            commands exactly as a real login would)
 *  - ``useNavigate()``     → action navigation
 *  - ``useTheme()``        → the existing dark/light toggle
 *
 * Returns the gated command list + a ``filter(query)`` that ranks matches. The
 * visual palette (issue #1515) consumes this hook and these types only.
 */
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions, useEffectiveAuth, useAuth } from "../../contexts/auth";
import { useModules } from "../../contexts/modules";
import { useTheme } from "../../hooks/useTheme";
import { isBackOfficePreset, worksFinanceDesk, worksParalegalDesk } from "../../components/layout/crmNav";
import { buildNavCommands } from "./navCommands";
import { buildActionCommands } from "./actionCommands";
import { filterCommands } from "./filter";
import type { CommandContext, CommandEntry, RankedMatch } from "./types";

export interface CommandRegistry {
  commands: CommandEntry[];
  filter: (query: string) => RankedMatch[];
}

export function useCommandRegistry(): CommandRegistry {
  const { preset, has, farmWideCanvass, hasOwnBook } = usePermissions();
  const { isEnabled } = useModules();
  const { role } = useEffectiveAuth();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();

  const ctx: CommandContext = useMemo(
    () => ({
      role,
      isEnabled,
      has,
      isBackOffice: isBackOfficePreset(preset),
      farmWideCanvass,
      hasOwnBook,
      paralegalDesk: worksParalegalDesk(role, preset),
      financeDesk: worksFinanceDesk(role, preset),
      isSuperuser: Boolean(user?.is_superuser),
    }),
    [role, isEnabled, has, preset, farmWideCanvass, hasOwnBook, user?.is_superuser],
  );

  const commands = useMemo<CommandEntry[]>(() => {
    const nav = buildNavCommands(ctx);
    const actions = buildActionCommands(ctx, {
      navigate: (url) => navigate(url),
      toggleTheme,
    });
    return [...nav, ...actions];
  }, [ctx, navigate, toggleTheme]);

  const filter = useCallback(
    (query: string) => filterCommands(commands, query),
    [commands],
  );

  return { commands, filter };
}
